import React, { useEffect, useState } from 'react';
import { followPolicyLink } from '../utils/legal';
import {
  PACARION_WORDMARK,
  SELLER_KEEP_NOTE,
  CONTACT_STRIP_NOTE,
  transferConsentNotice,
  claimConsentNotice,
  buyerEmailDecision,
} from '../shared/homeTransfer';
import { ReportFooter, ReportWordmark } from './HomeHistoryReportBody';
import { PACARION_TAGLINE } from '../shared/sponsorlessBrand';

const SPONSOR_HELP = 'A sponsor can see your property details, tasks, photos, documents, and the pros you add, but never your AI conversations or private notes. You can change this later.';
const SELLER_AGENT_NOTE = 'The buyer chooses their own sponsor, or continues without one. Your sponsor is not assigned to them.';

function palette(colors) {
  return {
    text: (colors && colors.text) || '#1a1a1a',
    muted: (colors && colors.textSecondary) || '#666',
    surface: (colors && colors.surface) || '#fff',
    border: (colors && colors.border) || '#e6e6e6',
    background: (colors && colors.background) || '#fff',
  };
}

function wordmarkStyle(color) {
  return {
    margin: '0 0 0.85rem',
    fontWeight: 300,
    letterSpacing: '0.18em',
    fontSize: '0.95rem',
    color,
  };
}

function buttonStyle(disabled) {
  return {
    background: disabled ? '#c8c8c8' : '#59c4c4',
    color: '#fff',
    border: 'none',
    borderRadius: '0.5rem',
    minHeight: '44px',
    padding: '0.7rem 1rem',
    fontWeight: 650,
    cursor: disabled ? 'default' : 'pointer',
    width: '100%',
    maxWidth: '20rem',
  };
}

function fieldStyle(tone) {
  return {
    display: 'block',
    width: '100%',
    boxSizing: 'border-box',
    minHeight: '44px',
    marginTop: '0.3rem',
    border: `1px solid ${tone.border}`,
    borderRadius: '0.5rem',
    padding: '0.55rem 0.7rem',
    background: tone.surface,
    color: tone.text,
    font: 'inherit',
  };
}

function noticeParts(text) {
  const parts = String(text || '').split('\n\n');
  return {
    title: parts[0] || '',
    body: parts[1] || '',
    checkbox: parts[2] || '',
  };
}

export function TransferWordmark({ color }) {
  return <p style={wordmarkStyle(color)}>{PACARION_WORDMARK}</p>;
}

export function HomeTransferSellerForm({
  colors,
  reports,
  initialClosingDate,
  initialEmail,
  env,
  sellerEmail,
  onSubmit,
}) {
  const tone = palette(colors);
  const list = reports || [];
  const [closingDate, setClosingDate] = useState(initialClosingDate || '');
  const [buyerEmail, setBuyerEmail] = useState(initialEmail || '');
  const [reportId, setReportId] = useState(list[0] ? list[0].id : '');
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [transferId, setTransferId] = useState('');

  useEffect(() => {
    if (!reportId && reports && reports[0]) setReportId(reports[0].id);
  }, [reports, reportId]);

  const selected = list.filter((item) => item.id === reportId)[0] || list[0] || null;
  const copy = noticeParts(transferConsentNotice(selected ? {
    reportNumber: selected.reportNumber,
    version: selected.version,
    documentCount: selected.documentCount,
    photoCount: selected.photoCount,
  } : null));
  const source = env || (typeof process !== 'undefined' && process.env) || {};
  const emailDecision = buyerEmailDecision(buyerEmail, source, sellerEmail);
  const emailReady = emailDecision.ok;
  const dateReady = /^\d{4}-\d{2}-\d{2}$/.test(closingDate);
  const ready = Boolean(accepted && emailReady && dateReady && selected && !busy && !transferId);

  async function submit(event) {
    event.preventDefault();
    if (!ready || typeof onSubmit !== 'function') return;
    setBusy(true);
    setNotice('');
    try {
      const data = await onSubmit({
        closingDate,
        buyerEmail: emailDecision.email,
        reportId: selected.id,
      });
      setTransferId((data && data.transferId) || 'sent');
      setNotice(data && data.emailed === false
        ? 'The hand-off is saved. The email could not be sent.'
        : 'Sent to the buyer.');
    } catch (error) {
      setNotice((error && error.message) || 'The home could not be sent.');
    } finally {
      setBusy(false);
    }
  }

  async function cancel() {
    if (!transferId || transferId === 'sent' || typeof onSubmit !== 'function') return;
    setBusy(true);
    try {
      await onSubmit({ cancel: true, transferId });
      setNotice('Hand-off cancelled. The buyer can no longer claim it.');
      setTransferId('');
      setAccepted(false);
    } catch (error) {
      setNotice((error && error.message) || 'The hand-off could not be cancelled.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form data-testid="home-transfer-seller" onSubmit={submit} style={{ color: tone.text, maxWidth: '40rem' }}>
      <h2 style={{ fontWeight: 400, fontSize: '1.35rem', margin: '0 0 0.5rem' }}>{copy.title}</h2>
      <p style={{ lineHeight: 1.5 }}>{copy.body}</p>
      <p style={{ lineHeight: 1.5 }}>{SELLER_KEEP_NOTE}</p>
      <p style={{ lineHeight: 1.5 }}>{SELLER_AGENT_NOTE}</p>
      <p style={{ lineHeight: 1.5 }}>{CONTACT_STRIP_NOTE}</p>
      <label style={{ display: 'block', marginBottom: '0.75rem' }}>
        Closing date
        <input
          type="date"
          value={closingDate}
          onChange={(event) => setClosingDate(event.target.value)}
          style={fieldStyle(tone)}
        />
      </label>
      <p style={{ color: tone.muted, marginTop: 0 }}>Send this only after the sale has closed.</p>
      <label style={{ display: 'block', marginBottom: '0.75rem' }}>
        Buyer email
        <input
          type="email"
          inputMode="email"
          autoComplete="email"
          value={buyerEmail}
          onChange={(event) => setBuyerEmail(event.target.value)}
          style={fieldStyle(tone)}
        />
      </label>
      {buyerEmail && !emailReady ? <p role="alert">{emailDecision.error}</p> : null}
      <label style={{ display: 'block', marginBottom: '0.75rem' }}>
        Report to send
        <select
          aria-label="Report to send"
          value={selected ? selected.id : ''}
          onChange={(event) => setReportId(event.target.value)}
          style={fieldStyle(tone)}
        >
          {list.map((item) => (
            <option key={item.id} value={item.id}>
              {item.reportNumber}{item.version ? ` v${item.version}` : ''}
            </option>
          ))}
        </select>
      </label>
      <label style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-start', lineHeight: 1.45 }}>
        <input
          type="checkbox"
          checked={accepted}
          onChange={(event) => setAccepted(event.target.checked)}
          style={{ marginTop: '0.25rem', minWidth: '1.1rem', minHeight: '1.1rem' }}
        />
        <span>{copy.checkbox}</span>
      </label>
      <p>
        <a href="/terms" onClick={followPolicyLink}>Terms</a>
        {' · '}
        <a href="/privacy" onClick={followPolicyLink}>Privacy Policy</a>
      </p>
      {notice ? <p role="status">{notice}</p> : null}
      <button type="submit" style={buttonStyle(!ready)} disabled={!ready}>
        Send to buyer
      </button>
      {transferId && transferId !== 'sent' ? (
        <button type="button" style={{ ...buttonStyle(busy), marginTop: '0.6rem', background: 'transparent', color: tone.text, border: `1px solid ${tone.border}` }} disabled={busy} onClick={cancel}>
          Cancel hand-off
        </button>
      ) : null}
    </form>
  );
}

export function HomeTransferClaimForm({
  colors,
  addressLabel,
  onSubmit,
}) {
  const tone = palette(colors);
  const copy = noticeParts(claimConsentNotice(addressLabel));
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [choice, setChoice] = useState('');
  const [code, setCode] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const codeReady = choice !== 'code' || /^[A-Za-z0-9]{6}$/.test(code.trim());
  const ready = Boolean(
    accepted
    && (choice === 'sponsorless' || choice === 'code')
    && codeReady
    && name.trim().length >= 2
    && password.length >= 8
    && !busy
  );

  async function submit(event) {
    event.preventDefault();
    if (!ready || typeof onSubmit !== 'function') return;
    setBusy(true);
    setNotice('');
    try {
      await onSubmit({
        name: name.trim(),
        password,
        sponsorChoice: choice,
        sponsorCode: choice === 'code' ? code.trim().toUpperCase() : '',
      });
    } catch (error) {
      setNotice((error && error.message) || 'The home could not be claimed.');
      setBusy(false);
    }
  }

  return (
    <form data-testid="home-transfer-claim" onSubmit={submit} style={{ color: tone.text, maxWidth: '40rem' }}>
      <h1 style={{ fontWeight: 400, fontSize: '1.45rem', margin: '0 0 0.5rem' }}>{copy.title}</h1>
      <p style={{ lineHeight: 1.5 }}>{copy.body}</p>
      <label style={{ display: 'block', marginBottom: '0.75rem' }}>
        Your name
        <input value={name} autoComplete="name" onChange={(event) => setName(event.target.value)} style={fieldStyle(tone)} />
      </label>
      <label style={{ display: 'block', marginBottom: '0.75rem' }}>
        Password
        <input type="password" autoComplete="new-password" value={password} onChange={(event) => setPassword(event.target.value)} style={fieldStyle(tone)} />
      </label>
      <fieldset style={{ border: `1px solid ${tone.border}`, borderRadius: '0.75rem', padding: '0.85rem 1rem', margin: '0 0 1rem' }}>
        <legend style={{ fontWeight: 650, padding: '0 0.35rem' }}>Who should sponsor your Pacarion account?</legend>
        <label style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-start', margin: '0.65rem 0' }}>
          <input
            type="radio"
            name="buyer-sponsor"
            value="sponsorless"
            checked={choice === 'sponsorless'}
            onChange={() => setChoice('sponsorless')}
          />
          <span>Continue without a sponsor (free)</span>
        </label>
        <label style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-start', margin: '0.65rem 0' }}>
          <input
            type="radio"
            name="buyer-sponsor"
            value="code"
            checked={choice === 'code'}
            onChange={() => setChoice('code')}
          />
          <span>I have a sponsor code</span>
        </label>
        <label style={{ display: 'block', marginTop: '0.35rem' }}>
          Sponsor code
          <input
            aria-label="Sponsor code"
            value={code}
            maxLength={6}
            autoCapitalize="characters"
            disabled={choice !== 'code'}
            onChange={(event) => setCode(event.target.value.toUpperCase())}
            style={fieldStyle(tone)}
          />
        </label>
        <p style={{ color: tone.muted, lineHeight: 1.45 }}>{SPONSOR_HELP}</p>
      </fieldset>
      <label style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-start', lineHeight: 1.45 }}>
        <input
          type="checkbox"
          checked={accepted}
          onChange={(event) => setAccepted(event.target.checked)}
          style={{ marginTop: '0.25rem', minWidth: '1.1rem', minHeight: '1.1rem' }}
        />
        <span>{copy.checkbox}</span>
      </label>
      <p>
        <a href="/terms" onClick={followPolicyLink}>Terms of Service</a>
        {' · '}
        <a href="/privacy" onClick={followPolicyLink}>Privacy Policy</a>
      </p>
      {notice ? <p role="alert">{notice}</p> : null}
      <button type="submit" style={buttonStyle(!ready)} disabled={!ready}>
        Claim home
      </button>
    </form>
  );
}

export function HomeBuyerHistory({ colors, address, city, state, note, entries }) {
  const tone = palette(colors);
  const rows = entries || [];
  const place = [address, city, state].filter(Boolean).join(', ');
  return (
    <section data-testid="home-buyer-history" style={{ color: tone.text, maxWidth: '40rem' }}>
      <div style={{
        background: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: '16px',
        padding: '1.1rem 1rem 1.15rem',
      }}
      >
        <ReportWordmark />
        <p style={{ margin: '0.7rem 0 0', color: '#64748b' }}>{PACARION_TAGLINE}</p>
        {place ? <h1 style={{ fontWeight: 650, fontSize: '1.45rem', letterSpacing: '-0.03em', color: '#0f172a', margin: '0.85rem 0 0.4rem' }}>{place}</h1> : null}
        <p style={{ margin: '0.35rem 0 0', color: tone.muted }}>
          {rows.length} {rows.length === 1 ? 'record' : 'records'} from the previous owner
        </p>
      </div>
      <p style={{
        borderTop: `1px solid ${tone.border}`,
        borderBottom: `1px solid ${tone.border}`,
        padding: '0.85rem 0',
        lineHeight: 1.5,
        fontWeight: 650,
      }}
      >
        {note}
      </p>
      <ol style={{ listStyle: 'none', padding: 0, margin: '1rem 0 0' }}>
        {rows.map((entry) => (
          <li key={entry.id || entry.title} style={{ display: 'grid', gridTemplateColumns: '16px 1fr', gap: '0.75rem' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#0f172a', marginTop: 8 }} />
              <span style={{ width: 2, flex: 1, background: '#e2e8f0', minHeight: 12 }} />
            </div>
            <div style={{
              background: tone.surface,
              border: `1px solid ${tone.border}`,
              borderRadius: '0.75rem',
              padding: '0.9rem 1rem',
              marginBottom: '0.75rem',
            }}
            >
              <div style={{ fontWeight: 650 }}>{entry.title}</div>
              {entry.detail ? <p style={{ margin: '0.35rem 0' }}>{entry.detail}</p> : null}
              <p style={{ margin: 0, color: tone.muted }}>Recorded by the previous owner</p>
            </div>
          </li>
        ))}
      </ol>
      <ReportFooter />
    </section>
  );
}
